import React, { useState } from "react";
import { Table, Button, Space, Card, Divider, Input, Tabs, Typography } from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  PlusCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
const { Title } = Typography;

const MarketingSKUTable = ({
  selectedOfferPricings,
  handleAddOrEditPricing,
  offerPricingsOptions,
  setSelectedOfferPricings,
  scrollToSection,
  setShowIndependentSKU,
  setCurrentPricingId,
}) => {
  const [searchText, setSearchText] = useState("");

  const handleDeletePricing = (id) => {
    const MySwal = withReactContent(Swal);
    MySwal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this SKU from the selection?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        try {
          // Just remove the SKU from the selected data
          const updatedPricings = selectedOfferPricings.filter((pricing) => pricing.value !== id);
          setSelectedOfferPricings(updatedPricings);

          return true;
        } catch (error) {
          console.error("Error removing SKU from selection:", error);
          MySwal.showValidationMessage(`Error: ${error.message}`);
          return false;
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        MySwal.fire("Removed!", "The SKU has been removed from your selection.", "success");
      }
    });
  };

  // Define columns for the Ant Design Table
  const columns = [
    {
      title: "Marketing SKU",
      dataIndex: ["object", "sku"],
      key: "sku",
      fixed: "left",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Marketing Dependent SKU",
      dataIndex: "dependentSku",
      key: "dependentSku",
    },
    {
      title: "Item Variation SKU",
      dataIndex: "variationCode",
      key: "variationCode",
    },
    {
      title: "Category",
      dataIndex: "productType",
      key: "productType",
    },
    {
      title: "Unit Description",
      dataIndex: "unitDescription",
      key: "unitDescription",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Qty Detailed",
      dataIndex: "quantityDetailed",
      key: "quantityDetailed",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Price",
      dataIndex: "originalPrice",
      key: "originalPrice",
      render: (text) => `$${text}`,
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      render: (text) => `$${text}`,
    },
    {
      title: "Price to Pay",
      dataIndex: "finalPrice",
      key: "finalPrice",
      render: (text) => `$${text}`,
    },
    {
      title: "Rule",
      dataIndex: "rule",
      key: "rule",
    },
    {
      title: "Optional",
      dataIndex: "optional",
      key: "optional",
    },
    {
      title: "Bundle Price",
      dataIndex: ["object", "total_bundle_price"],
      key: "totalBundlePrice",
    },
    {
      title: "Bundle Discount",
      dataIndex: ["object", "bundle_discount"],
      key: "bundleDiscount",
    },
    {
      title: "Bundle Price to Pay",
      dataIndex: ["object", "bundle_price_to_pay"],
      key: "bundlePriceToPay",
    },
    {
      title: "Bundle Rule",
      dataIndex: ["object", "bundle_rule"],
      key: "bundleRule",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              scrollToSection("offer-status-section");
              handleAddOrEditPricing(record.object?.id);
            }}
            title="Edit Items"
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              if (record.pId) {
                handleDeletePricing(record.pId);
              } else {
                const updatedSelection = selectedOfferPricings.filter(
                  (p) => p.value !== record.object?.id
                );
                setSelectedOfferPricings(updatedSelection);
              }
            }}
            title="Delete Offer SKU"
          />
        </Space>
      ),
    },
  ];

  // Transform data for the Ant Design Table
  const transformData = () => {
    const data = [];

    selectedOfferPricings.forEach((pricing) => {
      if (pricing.object?.marketing_dependent_skus_details?.length > 0) {
        // Add the main row with the first dependent SKU
        const firstItem = pricing.object.marketing_dependent_skus_details[0];
        data.push({
          key: pricing.object?.id,
          object: pricing.object,
          pId: pricing.pId,
          dependentSku: firstItem?.sku,
          variationCode: firstItem?.variation_code,
          productType: firstItem?.product_type,
          unitDescription: firstItem?.unit_description,
          quantity: firstItem?.quantity,
          quantityDetailed: firstItem?.quantity_detailed,
          description: firstItem?.description,
          originalPrice: firstItem?.original_price,
          discount: firstItem?.discount,
          finalPrice: firstItem?.final_price,
          rule: firstItem?.rule,
          optional: firstItem?.optional,
          isParent: true,
        });

        // Add child rows for additional dependent SKUs
        pricing.object.marketing_dependent_skus_details.slice(1).forEach((item, index) => {
          data.push({
            key: `${pricing.object?.id}-${index}`,
            object: pricing.object,
            dependentSku: item.sku,
            variationCode: item.variation_code,
            productType: item.product_type,
            unitDescription: item.unit_description,
            quantity: item.quantity,
            quantityDetailed: item.quantity_detailed,
            description: item.description,
            originalPrice: item.original_price,
            discount: item.discount,
            finalPrice: item.final_price,
            rule: item.rule,
            optional: item.optional,
            isChild: true,
            parentKey: pricing.object?.id,
          });
        });
      } else {
        // Add a row for pricing without dependent SKUs
        data.push({
          key: pricing.object?.id,
          object: pricing.object,
          pId: pricing.pId,
          dependentSku: "No items available",
          isParent: true,
        });
      }
    });

    return data;
  };

  return (
    <Card id="offer-pricing-section" style={{ marginTop: 16 }}>
      <Title level={5}>Marketing SKU</Title>
      <Table
        columns={columns}
        dataSource={transformData()}
        bordered
        size="middle"
        scroll={{ x: "max-content" }}
        pagination={false}
        className="marketing-sku-table"
        style={{
          // Add specific styles to fix header overlap issues
          "--antd-wave-shadow-color": "transparent",
          "--scroll-bar-size": "12px",
        }}
      />

      {/* Add CSS to fix the overlapping headers */}
      <style>{`
        .marketing-sku-table .ant-table-cell {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .marketing-sku-table .ant-table-cell-fix-left,
        .marketing-sku-table .ant-table-cell-fix-right {
          z-index: 2;
        }
        
        .marketing-sku-table .ant-table-thead .ant-table-cell-fix-left,
        .marketing-sku-table .ant-table-thead .ant-table-cell-fix-right {
          z-index: 3;
          background-color: #f0f0f0;
        }
      `}</style>
      <Divider />
      <Tabs
        defaultActiveKey="1"
        items={[
          {
            label: "Available SKUs",
            key: "1",
            children: (
              <>
                <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
                  <Input.Search
                    placeholder="Search SKUs"
                    style={{ width: 300 }}
                    onSearch={(value) => {
                      setSearchText(value);
                    }}
                    onChange={(e) => {
                      if (!e.target.value) {
                        setSearchText("");
                      }
                    }}
                    onPressEnter={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSearchText(e.target.value);
                    }}
                  />
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={() => {
                      setShowIndependentSKU(true);
                      setCurrentPricingId(null);
                    }}>
                    Create Marketing SKU
                  </Button>
                </div>
                <AvailableSkusTable
                  offerPricingsOptions={offerPricingsOptions}
                  selectedOfferPricings={selectedOfferPricings}
                  setSelectedOfferPricings={setSelectedOfferPricings}
                  handleAddOrEditPricing={handleAddOrEditPricing}
                  scrollToSection={scrollToSection}
                  searchText={searchText}
                />
              </>
            ),
          },
        ]}>
        {/* <Tabs.TabPane tab="Available SKUs" key="1">
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
            <Input.Search
              placeholder="Search SKUs"
              style={{ width: 300 }}
              onSearch={(value) => {
                setSearchText(value);
              }}
              onChange={(e) => {
                if (!e.target.value) {
                  setSearchText("");
                }
              }}
              onPressEnter={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setSearchText(e.target.value);
              }}
            />
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => {
                setShowIndependentSKU(true);
                setCurrentPricingId(null);
              }}>
              Create Marketing SKU
            </Button>
          </div>
          <AvailableSkusTable
            offerPricingsOptions={offerPricingsOptions}
            selectedOfferPricings={selectedOfferPricings}
            setSelectedOfferPricings={setSelectedOfferPricings}
            handleAddOrEditPricing={handleAddOrEditPricing}
            scrollToSection={scrollToSection}
            searchText={searchText}
          />
        </Tabs.TabPane> */}
      </Tabs>
    </Card>
  );
};

// Table component for available SKUs that can be selected
const AvailableSkusTable = ({
  offerPricingsOptions,
  selectedOfferPricings,
  setSelectedOfferPricings,
  handleAddOrEditPricing,
  scrollToSection,
  searchText,
}) => {
  // Filter out SKUs that are already selected
  const availableSkus = offerPricingsOptions
    .filter((option) => !selectedOfferPricings.some((selected) => selected.value === option.value))
    .map((sku) => ({ ...sku, key: sku.value }));

  const handleAddSku = (sku) => {
    setSelectedOfferPricings([...selectedOfferPricings, sku]);
  };

  const columns = [
    {
      title: "Marketing SKU",
      dataIndex: "label",
      key: "sku",
      fixed: "left",
      width: 180,
      ellipsis: true,
      sorter: (a, b) => a.label.localeCompare(b.label),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) => record.label.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Marketing Dependent SKU",
      dataIndex: ["object", "marketing_dependent_skus_details", 0, "sku"],
      key: "dependentSku",
      render: (text, record) => {
        const firstItem = record.object?.marketing_dependent_skus_details?.[0];
        return firstItem?.sku || "No items available";
      },
    },
    {
      title: "Item Variation SKU",
      dataIndex: ["object", "marketing_dependent_skus_details", 0, "variation_code"],
      key: "variationCode",
      render: (text, record) => {
        const firstItem = record.object?.marketing_dependent_skus_details?.[0];
        return firstItem?.variation_code || "-";
      },
    },
    {
      title: "Category",
      dataIndex: ["object", "marketing_dependent_skus_details", 0, "product_type"],
      key: "productType",
      render: (text, record) => {
        const firstItem = record.object?.marketing_dependent_skus_details?.[0];
        return firstItem?.product_type || "-";
      },
    },
    {
      title: "Unit Description",
      dataIndex: ["object", "marketing_dependent_skus_details", 0, "unit_description"],
      key: "unitDescription",
      render: (text, record) => {
        const firstItem = record.object?.marketing_dependent_skus_details?.[0];
        return firstItem?.unit_description || "-";
      },
    },
    {
      title: "Bundle Price",
      dataIndex: ["object", "total_bundle_price"],
      key: "totalBundlePrice",
      render: (text) => (text ? `$${text}` : "-"),
    },
    {
      title: "Bundle Discount",
      dataIndex: ["object", "bundle_discount"],
      key: "bundleDiscount",
      render: (text) => (text ? `$${text}` : "-"),
    },
    {
      title: "Bundle Price to Pay",
      dataIndex: ["object", "bundle_price_to_pay"],
      key: "bundlePriceToPay",
      render: (text) => (text ? `$${text}` : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleAddSku(record)}
            title="Add to selected">
            Add
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ height: "300px", overflow: "auto" }}>
        <Table
          columns={columns}
          dataSource={availableSkus}
          rowKey="value"
          pagination={false}
          size="small"
          scroll={{ x: "max-content", y: 250 }}
          className="available-sku-table"
          style={{
            "--antd-wave-shadow-color": "transparent",
            "--scroll-bar-size": "12px",
          }}
        />
      </div>

      {/* Add CSS to fix the overlapping headers */}
      <style>{`
        .available-sku-table .ant-table-cell {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .available-sku-table .ant-table-cell-fix-left,
        .available-sku-table .ant-table-cell-fix-right {
          z-index: 2;
        }
        
        .available-sku-table .ant-table-thead .ant-table-cell-fix-left,
        .available-sku-table .ant-table-thead .ant-table-cell-fix-right {
          z-index: 3;
          background-color: #f0f0f0;
        }
      `}</style>
    </>
  );
};

export default MarketingSKUTable;
